import { useEffect, useRef } from 'react';
import { safeGetRecord, safeGetArray, safeSetJSON } from '@/lib/safeStorage';
import { reportRoomVisit } from '@/hooks/useCommunityStats';

export const useRoomVisit = (roomId: string) => {
  const startTime = useRef(Date.now());

  useEffect(() => {
    // Record visit
    const visits = safeGetRecord<number>('room-visits');
    visits[roomId] = (visits[roomId] || 0) + 1;
    safeSetJSON('room-visits', visits);

    // Record visit with timestamp
    const visitLog = safeGetArray<{ roomId: string; timestamp: number }>('room-visit-log');
    visitLog.push({ roomId, timestamp: Date.now() });
    safeSetJSON('room-visit-log', visitLog.slice(-100)); // Keep last 100

    return () => {
      // Record duration on exit
      const duration = Date.now() - startTime.current;
      const durations = safeGetRecord<number[]>('room-durations');
      if (!durations[roomId]) durations[roomId] = [];
      durations[roomId].push(duration);
      safeSetJSON('room-durations', durations);

      // Report to community stats (fire-and-forget)
      const timeSeconds = Math.floor(duration / 1000);
      if (timeSeconds > 0) {
        reportRoomVisit(roomId, timeSeconds);
      }

      // Track early exits (under 5 seconds)
      if (duration < 5000) {
        const earlyExits = safeGetArray<{ roomId: string; duration: number; timestamp: number }>('early-exits');
        earlyExits.push({ roomId, duration, timestamp: Date.now() });
        safeSetJSON('early-exits', earlyExits.slice(-20));
      }
    };
  }, [roomId]);
};

export const getRoomVisitCounts = (): Record<string, number> => {
  return safeGetRecord<number>('room-visits');
};

export const getRoomDurations = (): Record<string, number[]> => {
  return safeGetRecord<number[]>('room-durations');
};

export const getEarlyExits = (): Array<{ roomId: string; duration: number; timestamp: number }> => {
  return safeGetArray<{ roomId: string; duration: number; timestamp: number }>('early-exits');
};

export const getAllRoomIds = (): string[] => {
  const visitLog = safeGetArray<{ roomId: string }>('room-visit-log');
  return [...new Set(visitLog.map((v) => v.roomId))];
};
